/**
 * Rail Watch catalog — customer-selective names in the client rail.
 *
 * Free: 1 slot.
 * Operator+: 5 slots.
 *
 * Book names come from the open book. Other names come from the live
 * venue search — never a hardcoded tape.
 */

import type { PlanTier } from '@/lib/stripe/types'

export const WATCH_STORAGE_KEY = 'mechagora.watch.symbols.v1'
export const WATCH_CHANGE_EVENT = 'mechagora:watch-symbols'

/** Trade owns the scan. Rail on this route is pin source only. */
export function isTradeWatchRoute(pathname: string | null | undefined): boolean {
  const path = (pathname || '').trim()
  return path === '/trading' || path.startsWith('/trading/')
}

export function watchSlotCount(tier: PlanTier | 'none'): number {
  if (tier === 'none' || tier === 'free') return 1
  return 5
}

/** Live venue find keys — not a pin list. BTC/XBT and DOGE/XDG are the same coin. */
const CRYPTO_FIND: Record<string, readonly string[]> = {
  BTC: ['BTC', 'XBT'],
  XBT: ['BTC', 'XBT'],
  BITCOIN: ['BTC', 'XBT'],
  ETH: ['ETH'],
  ETHEREUM: ['ETH'],
  ETHER: ['ETH'],
  SOL: ['SOL'],
  SOLANA: ['SOL'],
  DOGE: ['XDG', 'DOGE'],
  DODGE: ['XDG', 'DOGE'],
  DOGECOIN: ['XDG', 'DOGE'],
  XDG: ['XDG', 'DOGE'],
}

const VENUE_BASE_LABELS: Record<string, string> = {
  XDG: 'DOGE',
  XBT: 'BTC',
}

const QUOTE_SUFFIXES = ['USDT', 'USDC', 'USD', 'EUR', 'GBP', 'AUD', 'CAD', 'JPY', 'XBT', 'BTC', 'ETH'] as const

export const CRYPTO_TAPE_PREVIEW = ['BTC', 'ETH', 'SOL'] as const

function compactFindKey(query: string): string {
  let q = query.trim().toUpperCase().replace(/[^A-Z0-9]/g, '')
  if (q.endsWith('USA')) q = `${q.slice(0, -3)}USD`
  return q
}

function findBaseKey(compact: string): string {
  if (CRYPTO_FIND[compact]) return compact
  for (const quote of QUOTE_SUFFIXES) {
    if (compact.endsWith(quote) && compact.length > quote.length) {
      const base = compact.slice(0, -quote.length)
      if (CRYPTO_FIND[base]) return base
    }
  }
  return compact
}

export function watchSearchNeedles(query: string): string[] {
  const compact = compactFindKey(query)
  if (compact.length < 2) return []
  const mapped = CRYPTO_FIND[findBaseKey(compact)]
  if (mapped) return [...mapped]
  return [compact]
}

/** Venue search wants a ticker. "dodge" / "doge/usa" → XDG. */
export function expandWatchSearchQuery(query: string): string {
  const needles = watchSearchNeedles(query)
  return needles[0] || query.trim().toUpperCase()
}

export function watchPairBase(symbol: string, display: string): string {
  const d = display.toUpperCase()
  const split = d.search(/[/-]/)
  if (split > 0) return d.slice(0, split)
  const s = symbol.toUpperCase()
  for (const quote of QUOTE_SUFFIXES) {
    if (s.endsWith(quote) && s.length > quote.length) {
      return s.slice(0, s.length - quote.length)
    }
  }
  return s
}

export function matchesWatchQuery(symbol: string, display: string, query: string): boolean {
  const needles = watchSearchNeedles(query)
  if (needles.length === 0) return true
  const base = watchPairBase(symbol, display)
  const sym = symbol.toUpperCase()
  return needles.some(
    (needle) => base === needle || base.startsWith(needle) || sym === needle
  )
}

/** US equity tape names are 1–5 letter tickers, not USD-suffixed venue pairs. */
export function isEquityTapeSymbol(symbol: string): boolean {
  const raw = symbol.trim().toUpperCase()
  if (!raw) return false
  if (/[/-]/.test(raw)) return false
  const compact = raw.replace(/[^A-Z0-9.]/g, '')
  for (const quote of QUOTE_SUFFIXES) {
    if (compact.endsWith(quote) && compact.length > quote.length) return false
  }
  const base = compact.replace(/\./g, '')
  if (CRYPTO_FIND[base]) return false
  return /^[A-Z]{1,5}(\.[A-Z])?$/.test(compact)
}

/** Kraken XDG/XBT → the name a trader typed (DOGE/BTC). */
export function watchDisplayName(symbol: string, display?: string): string {
  const source = (display || '').trim() || symbol
  let out = source
  for (const [venue, name] of Object.entries(VENUE_BASE_LABELS)) {
    out = out.replace(new RegExp(`\\b${venue}\\b`, 'gi'), name)
    if (new RegExp(`^${venue}`, 'i').test(out) && !new RegExp(`^${name}`, 'i').test(out)) {
      out = out.replace(new RegExp(`^${venue}`, 'i'), name)
    }
  }
  if (!/[/-]/.test(out)) {
    const upper = out.toUpperCase()
    for (const quote of QUOTE_SUFFIXES) {
      if (upper.endsWith(quote) && upper.length > quote.length) {
        return `${out.slice(0, out.length - quote.length)}/${quote}`
      }
    }
  }
  return out
}

/** Narrow rail face — drop common quote suffixes so DOGE/USD reads DOGE. */
export function railWatchLabel(symbol: string, display?: string): string {
  const full = watchDisplayName(symbol, display)
  const match = full.match(/^([A-Z0-9.]+)[/-](USD|USDT|USDC|EUR|GBP)$/i)
  if (match?.[1]) return match[1].toUpperCase()
  if (full.length <= 8) return full
  return full.slice(0, 7)
}

export function normalizeWatchSymbol(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  const symbol = raw.trim().toUpperCase()
  if (!/^[A-Z][A-Z0-9./-]{0,19}$/.test(symbol)) return null
  return symbol
}

export function normalizeWatchSelection(
  selected: string[],
  tier: PlanTier | 'none'
): string[] {
  const slots = watchSlotCount(tier)
  const unique: string[] = []
  for (const raw of selected) {
    const symbol = normalizeWatchSymbol(raw)
    if (!symbol || unique.includes(symbol)) continue
    unique.push(symbol)
  }
  return unique.slice(0, slots)
}

export function defaultWatchSelection(
  tier: PlanTier | 'none',
  bookSymbols: string[]
): string[] {
  const slots = watchSlotCount(tier)
  const unique: string[] = []
  for (const raw of bookSymbols) {
    const symbol = normalizeWatchSymbol(raw)
    if (!symbol || unique.includes(symbol)) continue
    unique.push(symbol)
    if (unique.length >= slots) break
  }
  return unique
}

export function hasWatchStorage(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(WATCH_STORAGE_KEY) != null
  } catch {
    return false
  }
}

export function readWatchSelection(tier: PlanTier | 'none'): string[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(WATCH_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return normalizeWatchSelection(
      parsed.filter((v): v is string => typeof v === 'string'),
      tier
    )
  } catch {
    return []
  }
}

export function writeWatchSelection(symbols: string[]): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(WATCH_STORAGE_KEY, JSON.stringify(symbols))
  } catch {
    /* ignore quota */
  }
  window.dispatchEvent(new CustomEvent(WATCH_CHANGE_EVENT, { detail: symbols }))
}

/** Same-tab pin edits. `storage` does not fire for the writer. */
export function subscribeWatchSelection(
  listener: (symbols: string[]) => void
): () => void {
  if (typeof window === 'undefined') return () => undefined
  const onChange = (event: Event) => {
    const detail = (event as CustomEvent<unknown>).detail
    if (!Array.isArray(detail)) return
    listener(detail.filter((value): value is string => typeof value === 'string'))
  }
  window.addEventListener(WATCH_CHANGE_EVENT, onChange)
  return () => window.removeEventListener(WATCH_CHANGE_EVENT, onChange)
}
